import type { ClientHealth, ClientStatusDto, RuleDto } from '@citilight-monitor/contracts';

export type StatusTone = 'good' | 'warning' | 'critical' | 'neutral';

/** Chip tones add "serious" (orange) so an unreachable client reads differently from a firing one. */
export type ChipTone = StatusTone | 'serious';

/** Chip tone and swatch colour per client health, in the order problems are listed. */
export const HEALTH_CHIP: Record<ClientHealth, { tone: ChipTone; swatch: string }> = {
  firing: { tone: 'critical', swatch: 'bg-critical' },
  unreachable: { tone: 'serious', swatch: 'bg-serious' },
  stale: { tone: 'warning', swatch: 'bg-warning' },
  no_data: { tone: 'neutral', swatch: 'bg-neutral' },
  healthy: { tone: 'good', swatch: 'bg-good' },
};

export interface MetricStatus {
  tone: StatusTone;
  label: string;
  rule: RuleDto;
}

export const HEALTH_LABELS: Record<ClientHealth, { label: string; tone: StatusTone }> = {
  healthy: { label: 'Healthy', tone: 'good' },
  firing: { label: 'Firing', tone: 'critical' },
  unreachable: { label: 'Unreachable', tone: 'critical' },
  stale: { label: 'Not reporting', tone: 'warning' },
  no_data: { label: 'No data yet', tone: 'neutral' },
};

/** Health sort order for tables: problems first. */
export const HEALTH_ORDER: Record<ClientHealth, number> = {
  firing: 0,
  unreachable: 1,
  stale: 2,
  no_data: 3,
  healthy: 4,
};

const TONE_RANK: Record<StatusTone, number> = { critical: 3, warning: 2, good: 1, neutral: 0 };

/**
 * Worst status across the rules on one metric:
 * firing → critical, counting breaches → warning "3 of 5", otherwise good. Undefined if no rule.
 */
export function metricStatus(status: ClientStatusDto, metricKey: string): MetricStatus | undefined {
  const states = new Map(status.ruleStates.map((s) => [s.ruleId, s]));
  let worst: MetricStatus | undefined;

  for (const rule of status.client.rules.filter((r) => r.metric === metricKey)) {
    const state = states.get(rule.id);
    const candidate: MetricStatus = state?.firing
      ? { tone: 'critical', label: 'Firing', rule }
      : state && state.consecutiveBreaches > 0
        ? { tone: 'warning', label: `Breaching · ${state.consecutiveBreaches} of ${rule.consecutive}`, rule }
        : { tone: 'good', label: 'Within limit', rule };
    if (!worst || TONE_RANK[candidate.tone] > TONE_RANK[worst.tone]) worst = candidate;
  }
  return worst;
}
